import _get from "lodash/get";
// eslint-disable-next-line import/no-cycle -- directive added automatically by Shepherd migration
import { getInstitution, getProduct } from './common'; // eslint-disable-next-line import/no-cycle -- directive added automatically by Shepherd migration

import { getNameWithFallbacks } from '../utils';
/**
 * Gets available term lengths
 *
 * @param  {Object} marketplaceEntity The marketplace entity
 *
 * @return {String} The available term lengths.
 */

export var getAvailableTermLengths = function getAvailableTermLengths(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.available_term_lengths.value');
};
/**
 * Get the bottom line text.
 *
 * @param  {Object} marketplaceEntity   The marketplace entity
 * @return {String}                     The bottom line value.
 */

export var getBottomLine = function getBottomLine(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.tagline.value');
};
/**
 * Gets the "cons" bullets
 *
 * @param  {Object} marketplaceEntity The marketplace entity
 *
 * @return {Array}  The "cons" array
 */

export var getConsBullets = function getConsBullets(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.cons_bullets.value');
};
/**
 * Get the disclaimer.
 *
 * @param {Object} marketplaceEntity The education loans entity.
 *
 * @return {string} The disclaimer.
 */

export var getDisclaimer = function getDisclaimer(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.disclaimer.value');
};
/**
 * Gets the name of the entity as the alt text for the associated image
 *
 * @param     {Object} marketplaceEntity  The marketplace entity
 * @return    {string}                    Alt text
 */

export var getImageAltText = function getImageAltText(marketplaceEntity) {
  return getName(marketplaceEntity);
};
/**
 * Get the source URL of the image associated with the offer.
 *
 * @param      {Object}  marketplaceEntity  The education loans entity
 * @return     {string}  The URL of the image.
 */

export var getImageSrc = function getImageSrc(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.image_source.value');
};
/**
 * Get the issuer.
 *
 * @param {Object} marketplaceEntity The marketplace entity.
 *
 * @return {string} Issuer name.
 */

export var getIssuerName = function getIssuerName(marketplaceEntity) {
  return _get(getInstitution(marketplaceEntity), 'name');
};
/**
 * Get the learn more link.
 *
 * @param {Object} marketplaceEntity The education loans entity.
 *
 * @return {string} Learn more link.
 */

export var getLearnMoreLink = function getLearnMoreLink(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.review_link.value');
};
/**
 * Get the maximum fixed annual percentage rate (APR).
 *
 * @param {Object} marketplaceEntity The education loans entity.
 *
 * @return {number} The maximum fixed APR.
 */

export var getMaxFixedAPR = function getMaxFixedAPR(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.max_fixed_apr.value');
};
/**
 * Get the maximum variable annual percentage rate (APR).
 *
 * @param {Object} marketplaceEntity The education loans entity.
 *
 * @return {number} The maximum variable APR.
 */

export var getMaxVariableAPR = function getMaxVariableAPR(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.max_variable_apr.value');
};
/**
 * Get the minimum recommended FICO score.
 *
 * @param {Object} marketplaceEntity The education loans entity.
 *
 * @return {string} The minimum recommended FICO score.
 */

export var getMinFICO = function getMinFICO(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.display_min_fico.value');
};
/**
 * Get the minimum fixed annual percentage rate (APR).
 *
 * @param {Object} marketplaceEntity The education loans entity.
 *
 * @return {number} The minimum fixed APR.
 */

export var getMinFixedAPR = function getMinFixedAPR(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.min_fixed_apr.value');
};
/**
 * Get the minimum variable annual percentage rate (APR).
 *
 * @param {Object} marketplaceEntity The education loans entity.
 *
 * @return {number} The minimum variable APR.
 */

export var getMinVariableAPR = function getMinVariableAPR(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.min_variable_apr.value');
};
/**
 * Get the name to display for an offer.
 *
 * @param      {Object}  marketplaceEntity  The education loans entity
 * @return     {string}  The offer name.
 */

export var getName = function getName(marketplaceEntity) {
  return getNameWithFallbacks(marketplaceEntity);
};
/**
 * Gets the "pros" bullets
 *
 * @param  {Object} marketplaceEntity The marketplace entity
 *
 * @return {Array}  The "pros" array
 */

export var getProsBullets = function getProsBullets(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.pros_bullets.value');
};
/**
 * Get the qualifications bullets.
 *
 * @param      {Object}  marketplaceEntity  The education loans entity
 * @return     {Array}   The qualifications.
 */

export var getQualificationsBullets = function getQualificationsBullets(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.qualifications_bullets.value');
};
/**
 * Get the slug.
 *
 * @param {Object} marketplaceEntity The education loans entity.
 * @return {string} The slug.
 */

export var getSlug = function getSlug(marketplaceEntity) {
  return _get(getProduct(marketplaceEntity), 'external_id');
};
/**
 * Get the star rating.
 *
 * @param      {Object}  marketplaceEntity  The education loans entity.
 * @return     {number}  The star rating.
 */

export var getStarRating = function getStarRating(marketplaceEntity) {
  // Education loans store the star rating as a string,
  // but all libraries expect a number, so convert here
  var starRating = _get(marketplaceEntity, 'product.details.star_rating.value');

  return starRating && parseFloat(starRating);
};
/**
 * Get sub product type.
 *
 * @param {Object}  marketplaceEntity  The education loans entity.
 * @return {String}                    The sub product type.
 */

export var getSubProductType = function getSubProductType(marketplaceEntity) {
  return _get(getProduct(marketplaceEntity), 'details.type.value');
};
/**
 * Get the loan offer type classification.
 * @todo https://nerdwallet.atlassian.net/browse/SHOP-10043
 *
 * @param {Object}  marketplaceEntity  The education loans entity.
 * @return {String}                    The loan sub-type.
 */

export var getOfferType = function getOfferType(marketplaceEntity) {
  return _get(marketplaceEntity, 'details.type.value');
};